import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { navigateTo } from '../router';
import { AgentSkillSection } from './AgentSkillSection';

// ── Reusable primitives ───────────────────────────────────────────────────────

function Badge({
  children,
  color = 'indigo',
}: {
  children: React.ReactNode;
  color?: 'indigo' | 'green' | 'amber' | 'sky';
}) {
  const colors = {
    indigo: 'bg-accent-soft text-accent',
    green: 'bg-good/15 text-good',
    amber: 'bg-warn/15 text-warn',
    sky: 'bg-accent-soft text-accent',
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 font-mono text-2xs font-semibold uppercase tracking-wide ${colors[color]}`}
    >
      {children}
    </span>
  );
}

function InlineCode({ children }: { children: React.ReactNode }) {
  return <code className="bg-bg px-1.5 py-0.5 font-mono text-xs text-link">{children}</code>;
}

function CodeBlock({ title, code, lang }: { title?: string; code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="overflow-hidden border border-rule">
      {title && (
        <div className="barhead justify-between normal-case tracking-normal">
          <span className="truncate text-xs font-medium text-accent">{title}</span>
          <div className="flex flex-shrink-0 items-center gap-3">
            <span className="text-3xs uppercase tracking-wider text-dim">{lang}</span>
            <button onClick={handleCopy} className="text-2xs text-dim hover:text-ink">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
      <pre className="overflow-x-auto bg-code-bg p-4 text-sm leading-relaxed text-link">
        <code>{code}</code>
      </pre>
    </div>
  );
}

interface Param {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

function ParamTable({ params }: { params: Param[] }) {
  return (
    <div className="overflow-x-auto border border-rule">
      <table className="w-full min-w-[480px] text-left text-sm">
        <thead className="border-b border-rule bg-bg">
          <tr>
            <th className="px-4 py-2 text-xs font-medium text-dim">Name</th>
            <th className="px-4 py-2 text-xs font-medium text-dim">Type</th>
            <th className="px-4 py-2 text-xs font-medium text-dim">Required</th>
            <th className="px-4 py-2 text-xs font-medium text-dim">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-rule-soft">
          {params.map((p) => (
            <tr key={p.name}>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <InlineCode>{p.name}</InlineCode>
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap font-mono text-xs text-dim">{p.type}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                {p.required ? (
                  <Badge color="indigo">required</Badge>
                ) : (
                  <span className="text-xs text-dim">optional</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-sm text-dim">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ToolCardProps {
  id?: string;
  name: string;
  description: string;
  params: Param[];
  example?: { code: string };
}

function ToolCard({ id, name, description, params, example }: ToolCardProps) {
  return (
    <div id={id} className="scroll-mt-20 border border-rule bg-panel">
      <div className="flex flex-wrap items-center gap-3 border-b border-rule-soft px-5 py-4">
        <Badge color="sky">tool</Badge>
        <code className="break-all font-mono text-sm font-medium text-ink">{name}</code>
      </div>
      <div className="space-y-4 px-5 py-4">
        <p className="text-sm leading-relaxed text-dim">{description}</p>
        {params.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-dim">Parameters</p>
            <ParamTable params={params} />
          </div>
        )}
        {example && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-dim">Usage hint</p>
            <pre className="overflow-x-auto bg-bg px-4 py-3 font-mono text-xs leading-relaxed text-dim">
              {example.code}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Quickstart data ───────────────────────────────────────────────────────────

type ClientId = 'claude-code' | 'cursor' | 'windsurf' | 'vscode' | 'other';

const CLIENTS: {
  id: ClientId;
  label: string;
  monogram: string;
  installs: string;
  verify: string;
}[] = [
  {
    id: 'claude-code',
    label: 'Claude Code',
    monogram: 'CC',
    installs:
      'MCP config plus a local agent skill file, so Claude Code resolves versions and answers from evidence automatically.',
    verify: 'Run `claude mcp list` in your terminal — llmcontext should be listed as connected.',
  },
  {
    id: 'cursor',
    label: 'Cursor',
    monogram: 'Cu',
    installs:
      'MCP config plus a local agent skill file, so Cursor resolves versions and answers from evidence automatically.',
    verify: 'Open Cursor Settings → MCP — llmcontext should appear with a green status.',
  },
  {
    id: 'windsurf',
    label: 'Windsurf',
    monogram: 'W',
    installs:
      'MCP config plus a local agent skill file, so Windsurf resolves versions and answers from evidence automatically.',
    verify: 'Open the Windsurf MCP panel — llmcontext should appear with a green status.',
  },
  {
    id: 'vscode',
    label: 'VS Code / Copilot',
    monogram: 'VS',
    installs:
      'MCP config plus a reusable prompt file you can invoke when you want a grounded answer.',
    verify: 'Run "MCP: List Servers" from the command palette — llmcontext should be listed.',
  },
  {
    id: 'other',
    label: 'Other MCP',
    monogram: '⋯',
    installs:
      'Nothing automatic — add the MCP server to your client config manually with the JSON below.',
    verify: "Check your client's MCP server list — llmcontext should be listed as connected.",
  },
];

const FIRST_QUERY_PROMPT =
  'Use llmcontext as an evidence concierge. For next@16, find the signatures, behavior, constraints, and version-compatibility notes relevant to reading searchParams in App Router page components. Cite the retrieved sources, then apply that evidence to my code.';

// ── Sidebar / TOC ─────────────────────────────────────────────────────────────

const scrollToId = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SIDEBAR: { group: string; items: { label: string; target: string; client?: ClientId }[] }[] =
  [
    {
      group: 'Get started',
      items: [
        { label: 'Quickstart', target: 'quickstart' },
        { label: 'Choose your client', target: 'choose-client' },
        { label: 'Run your first query', target: 'first-query' },
        { label: 'Add private sources', target: 'private-sources' },
      ],
    },
    {
      group: 'Integrations',
      items: [
        { label: 'Claude Code', target: 'choose-client', client: 'claude-code' },
        { label: 'Cursor', target: 'choose-client', client: 'cursor' },
        { label: 'Windsurf', target: 'choose-client', client: 'windsurf' },
        { label: 'VS Code / Copilot', target: 'choose-client', client: 'vscode' },
        { label: 'Other MCP clients', target: 'choose-client', client: 'other' },
      ],
    },
    {
      group: 'Concepts',
      items: [
        { label: 'Query workflow', target: 'query-workflow' },
        { label: 'Agent skill', target: 'agent-skill' },
      ],
    },
    {
      group: 'Deployment',
      items: [
        { label: 'Authentication & API keys', target: 'private-sources' },
        { label: 'Self-hosting', target: 'self-host' },
      ],
    },
    {
      group: 'Reference',
      items: [
        { label: 'MCP server', target: 'reference' },
        { label: 'get_context', target: 'tool-get-context' },
        { label: 'search_packages', target: 'tool-search-packages' },
      ],
    },
  ];

const ON_THIS_PAGE: { label: string; target: string }[] = [
  { label: '1. Choose your client', target: 'choose-client' },
  { label: '2. Install llmcontext', target: 'install' },
  { label: '3. Verify the connection', target: 'verify' },
  { label: '4. Run your first query', target: 'first-query' },
  { label: '5. Add private sources', target: 'private-sources' },
];

// ── Page ─────────────────────────────────────────────────────────────────────

export function DocsPage() {
  const { deploymentMode } = useAuth();
  // The MCP server is its own host, served at the root path (no /mcp suffix).
  const mcpUrl = deploymentMode === 'saas' ? 'https://mcp.llmcontext.dev' : 'https://your-mcp-host';
  const installCommand = `curl -fsSL https://llmcontext.dev/install.sh | sh`;
  const mcpJson = `{
  "mcpServers": {
    "llmcontext": {
      "url": "${mcpUrl}"
    }
  }
}`;

  const [clientId, setClientId] = useState<ClientId>('claude-code');
  const [installDetailOpen, setInstallDetailOpen] = useState(false);
  const client = CLIENTS.find((c) => c.id === clientId) ?? CLIENTS[0];

  // /skills deep links land on this merged page — jump to the skill section.
  useEffect(() => {
    if (window.location.pathname.endsWith('/skills')) {
      document.getElementById('agent-skill')?.scrollIntoView({ block: 'start' });
    }
  }, []);

  const selectClient = (id: ClientId) => {
    setClientId(id);
    scrollToId('choose-client');
  };

  return (
    <div className="gap-10 py-8 lg:grid lg:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[200px_minmax(0,1fr)_180px]">
      {/* ── Left sidebar ── */}
      <nav aria-label="Docs sections" className="mb-10 hidden lg:mb-0 lg:block">
        <div className="sticky top-20 space-y-6">
          {SIDEBAR.map(({ group, items }) => (
            <div key={group}>
              <p className="mb-2 text-2xs font-semibold uppercase tracking-[0.12em] text-dim">
                {group}
              </p>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={`${group}:${item.label}`}>
                    <button
                      type="button"
                      onClick={() =>
                        item.client ? selectClient(item.client) : scrollToId(item.target)
                      }
                      className="block w-full text-left text-sm text-dim transition-colors hover:text-ink"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className="min-w-0 space-y-16">
        {/* Hero */}
        <section id="quickstart" className="scroll-mt-20">
          <p className="page-eyebrow">Get started</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Connect llmcontext to your coding agent
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-dim">
            llmcontext is a shared evidence concierge for coding agents. It gives every agent
            up-to-date knowledge about package APIs, usage, constraints, and version compatibility,
            grounded in a verified package or repository at an exact indexed version.
          </p>
          <p className="mt-3 max-w-2xl border-l-2 border-accent pl-4 text-sm leading-relaxed text-dim">
            Index the source once, then reuse the same evidence across agents, sessions, and your
            whole organization instead of making each agent repeat the same discovery work.
          </p>
        </section>

        {/* Step 1 — Choose your client */}
        <section id="choose-client" className="scroll-mt-20 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">1. Choose your client</h2>
            <p className="mt-1 text-sm leading-relaxed text-dim">
              Select the client you use — the steps below tailor themselves to it.
            </p>
          </div>
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5"
            role="group"
            aria-label="Choose your client"
          >
            {CLIENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={clientId === c.id}
                onClick={() => setClientId(c.id)}
                className={`flex flex-col items-center gap-2 border px-3 py-4 transition-colors ${
                  clientId === c.id
                    ? 'border-accent bg-accent-soft text-ink'
                    : 'border-rule bg-panel text-dim hover:border-ink/30 hover:text-ink'
                }`}
              >
                <span
                  aria-hidden
                  className={`flex h-8 w-8 items-center justify-center border font-mono text-xs font-bold ${clientId === c.id ? 'border-accent text-accent' : 'border-rule'}`}
                >
                  {c.monogram}
                </span>
                <span className="text-xs font-medium">{c.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-dim">You can change clients anytime.</p>
        </section>

        {/* Step 2 — Install */}
        <section id="install" className="scroll-mt-20 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">2. Install llmcontext</h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-dim">
              {clientId === 'other'
                ? 'Add the MCP server to your client config manually — any MCP-compatible client works.'
                : 'Run the installer. On the hosted service this is the fast path — no API key, no sign-up, and public packages just work.'}
            </p>
          </div>
          {clientId === 'other' ? (
            <CodeBlock title="mcp.json" lang="json" code={mcpJson} />
          ) : (
            <CodeBlock title="Terminal" lang="sh" code={installCommand} />
          )}
          <div className="flex items-start gap-2 text-sm leading-relaxed text-dim">
            <span aria-hidden className="mt-0.5 text-good">
              ✓
            </span>
            <span>
              For {client.label}, this sets up {client.installs}
            </span>
          </div>
          <div className="border border-rule">
            <button
              type="button"
              onClick={() => setInstallDetailOpen((v) => !v)}
              aria-expanded={installDetailOpen}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-link transition-colors hover:text-ink"
            >
              <span>What does this install?</span>
              <span aria-hidden className="text-dim">
                {installDetailOpen ? '▾' : '▸'}
              </span>
            </button>
            {installDetailOpen && (
              <div className="border-t border-rule-soft px-4 py-3 text-sm leading-relaxed text-dim">
                Claude Code, Cursor, and Windsurf get both MCP config and a local skill file. VS
                Code / Copilot gets MCP config plus a reusable prompt file you can invoke when
                needed. Other MCP clients use the manual config above. Working with private org
                repos or workspaces? Create an API key in your org settings, then answer yes when
                the installer asks about private organization access. For noninteractive setup, pipe
                it to <InlineCode>LLMCONTEXT_API_KEY=&lt;api_key&gt; sh</InlineCode>.
              </div>
            )}
          </div>
        </section>

        {/* Step 3 — Verify */}
        <section id="verify" className="scroll-mt-20 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">3. Verify the connection</h2>
            <p className="mt-1 text-sm leading-relaxed text-dim">
              Confirm that llmcontext is listed as connected in your client.
            </p>
          </div>
          <div className="flex items-center gap-3 border border-rule bg-panel px-4 py-4">
            <span
              aria-hidden
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-good/15 text-good"
            >
              ✓
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">Connected</p>
              <p className="text-sm leading-relaxed text-dim">{client.verify}</p>
            </div>
          </div>
        </section>

        {/* Step 4 — First query */}
        <section id="first-query" className="scroll-mt-20 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              4. Give the concierge its first brief
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-dim">
              Ask for the information your coding agent needs: the relevant APIs or behavior, the
              local task they matter to, and the constraints, examples, or compatibility notes to
              include. The concierge retrieves evidence; your coding agent still owns the edit and
              validation.
            </p>
          </div>
          <CodeBlock title="Try this prompt" lang="text" code={FIRST_QUERY_PROMPT} />
          <p className="text-sm leading-relaxed text-dim">
            Your agent should return an answer with sources like this:
          </p>
          <div className="border border-rule bg-panel">
            <div className="flex flex-wrap items-center gap-3 border-b border-rule-soft px-5 py-3">
              <span className="text-xs font-medium uppercase tracking-wider text-dim">
                Example response
              </span>
              <Badge color="green">cited answer</Badge>
            </div>
            <div className="space-y-4 px-5 py-4">
              <p className="text-sm leading-relaxed text-link">
                In Next.js 16 App Router, <InlineCode>searchParams</InlineCode> is a prop passed to
                page components as a Promise that resolves to a read-only object…
              </p>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-dim">Sources</p>
                <ol className="space-y-1.5">
                  <li className="flex items-baseline gap-2 font-mono text-xs text-dim">
                    <span className="flex-shrink-0 border border-rule px-1">1</span>
                    <span className="break-all">
                      next@16.0.0 · docs/app/api-reference/file-conventions/page.mdx
                    </span>
                  </li>
                  <li className="flex items-baseline gap-2 font-mono text-xs text-dim">
                    <span className="flex-shrink-0 border border-rule px-1">2</span>
                    <span className="break-all">
                      next@16.0.0 · docs/app/building-your-application/routing/query-parameters.mdx
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Step 5 — Private sources */}
        <section id="private-sources" className="scroll-mt-20 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              5. Add private sources <span className="font-normal text-dim">(optional)</span>
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-dim">
              Connect a workspace or create an API key to index private repositories.
            </p>
          </div>
          <div className="grid border border-rule bg-rule md:grid-cols-3 md:gap-px">
            <article className="bg-panel p-5">
              <h3 className="text-sm font-semibold text-ink">Index a private repository</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Connect GitHub, GitLab, or Azure DevOps and index your repos and docs at a pinned
                version.
              </p>
              <button
                type="button"
                onClick={() => navigateTo('/request')}
                className="mt-3 text-sm font-medium text-accent hover:underline"
              >
                Get started →
              </button>
            </article>
            <article className="border-t border-rule bg-panel p-5 md:border-t-0">
              <h3 className="text-sm font-semibold text-ink">API keys &amp; workspaces</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Create an API key and scope access with workspaces, so retrieval stays pinned to
                your versions.
              </p>
              <button
                type="button"
                onClick={() => navigateTo('/dashboard')}
                className="mt-3 text-sm font-medium text-accent hover:underline"
              >
                Learn more →
              </button>
            </article>
            <article className="border-t border-rule bg-panel p-5 md:border-t-0">
              <h3 className="text-sm font-semibold text-ink">Self-host llmcontext</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Bring llmcontext into your own environment with hands-on help from our team.
              </p>
              <button
                type="button"
                onClick={() => navigateTo('/contact')}
                className="mt-3 text-sm font-medium text-accent hover:underline"
              >
                Contact us →
              </button>
            </article>
          </div>
          <div className="flex flex-col gap-3 border border-accent/30 bg-accent-soft px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-ink">You&apos;re ready!</p>
              <p className="text-sm leading-relaxed text-dim">
                Your agent can now retrieve accurate, version-specific documentation with citations.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigateTo('/search')}
              className="flex-shrink-0 bg-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              Browse indexed packages
            </button>
          </div>
        </section>

        {/* Concepts — query workflow */}
        <section id="query-workflow" className="scroll-mt-20 space-y-4">
          <div>
            <p className="page-eyebrow">Concepts</p>
            <h2 className="mt-2 text-lg font-semibold text-ink">
              Discover when needed. Ask one focused question.
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-dim">
              Use <InlineCode>search_packages</InlineCode> when the package, registry, or indexed
              version is unknown. Use <InlineCode>get_context</InlineCode> once you can name the
              exact package and version. A useful brief follows this shape:{' '}
              <InlineCode>
                Find [APIs or behavior] relevant to [task], including [constraints, examples, or
                compatibility notes].
              </InlineCode>
            </p>
          </div>
          <CodeBlock
            title="A grounded package question"
            lang="text"
            code={`1. Read the exact version from the project lockfile.
2. If needed: search_packages({ query: "fastapi", registry: "pypi" })
3. Ask the concierge for evidence: get_context({ question: "Find the dependency injection signatures, lifecycle, and constraints relevant to route handlers.", registry: "pypi", package_name: "fastapi", version: "0.110.0", allow_nearby: false, answer: true, workspace_id: null })
4. The executor coding agent applies that evidence to the local codebase and validates the result.`}
          />
          <p className="text-sm leading-relaxed text-dim">
            Keep one topic per call. For compatibility between two packages, query each exact
            package version independently, in parallel when possible, then let the executor compare
            the two evidence sets against the local codebase.
          </p>
          <p className="text-sm leading-relaxed text-dim">
            No indexed match?{' '}
            <button
              type="button"
              onClick={() => navigateTo('/request')}
              className="font-medium text-accent hover:underline"
            >
              Request that exact package or repository version
            </button>{' '}
            so the same dated snapshot becomes available to the whole team, or{' '}
            <button
              type="button"
              onClick={() => navigateTo('/search')}
              className="font-medium text-accent hover:underline"
            >
              browse what&apos;s already indexed
            </button>
            .
          </p>
        </section>

        {/* Concepts — agent skill (also the /skills deep-link target) */}
        <AgentSkillSection />

        {/* Reference — MCP endpoint + tool parameters */}
        <section id="reference" className="scroll-mt-20 space-y-4">
          <div>
            <p className="page-eyebrow">Reference</p>
            <h2 className="mt-2 text-lg font-semibold text-ink">MCP server &amp; tools</h2>
            <p className="mt-1 text-sm leading-relaxed text-dim">
              Streamable HTTP at <InlineCode>{mcpUrl}</InlineCode>. Add to any MCP-compatible
              client.
            </p>
          </div>

          <CodeBlock title="mcp.json" lang="json" code={mcpJson} />

          <ToolCard
            id="tool-get-context"
            name="get_context"
            description={`Shared evidence concierge for the executor coding agent. Ask one focused question about an exact package version to retrieve current APIs, usage, constraints, examples, or compatibility evidence. The executor owns implementation and validation.`}
            params={[
              {
                name: 'question',
                type: 'string',
                required: true,
                description:
                  'One focused information brief: what API or behavior is needed, what task it informs, and which constraints, examples, errors, or compatibility notes to include. Do not ask it to write code.',
              },
              {
                name: 'registry',
                type: 'enum',
                required: true,
                description:
                  'Exact registry: npm, pypi, pub, crates, maven, github, gitlab, or ci. Take it from search_packages or the manifest.',
              },
              {
                name: 'package_name',
                type: 'string',
                required: true,
                description: 'Exact package name, or authenticated organization package alias.',
              },
              {
                name: 'version',
                type: 'string',
                required: true,
                description: 'Exact indexed or registry version.',
              },
              {
                name: 'allow_nearby',
                type: 'boolean',
                required: true,
                description: 'Allow a nearby indexed version while requesting the exact version.',
              },
              {
                name: 'answer',
                type: 'boolean',
                required: true,
                description: 'True returns a compact answer; false returns evidence chunks.',
              },
              {
                name: 'workspace_id',
                type: 'string | null',
                required: true,
                description: 'Workspace ID or slug, or null for all visible packages.',
              },
            ]}
            example={{
              code: `// Single-topic — exact package identity
get_context({
  question: "Find the relevant signatures, constraints, and examples for typing params and searchParams in App Router page components.",
  registry: "npm",
  package_name: "next",
  version: "16.0.0",
  allow_nearby: false,
  answer: true,
  workspace_id: null
})

// Indexed repo example
get_context({
  question: "Find the authentication flow, required headers, and validation constraints for webhook callers.",
  registry: "github",
  package_name: "api",
  version: "2024.11.0",
  allow_nearby: false,
  answer: true,
  workspace_id: null
})`,
            }}
          />

          <ToolCard
            id="tool-search-packages"
            name="search_packages"
            description={`Discover indexed packages, look up available versions, or triage whether a package has a specific feature — without fetching chunk content. Makes NO LLM calls and returns NO chunk content, so it's a cheap first step before get_context.`}
            params={[
              {
                name: 'query',
                type: 'string',
                required: true,
                description:
                  'Required. A package name, version question, or capability check, e.g. "latest version of fastapi", "does zod@4 have IP validation", "library for exponential backoff Python".',
              },
              {
                name: 'registry',
                type: "'npm' | 'pypi' | 'pub' | 'crates' | 'maven' | 'github' | 'gitlab'",
                required: false,
                description: 'Optional. Narrow results to a specific registry.',
              },
              {
                name: 'max_results',
                type: 'number',
                required: false,
                description: 'Optional. Maximum packages to return, 1-10. Default 5.',
              },
            ]}
            example={{
              code: `// Version lookup
search_packages({ query: "latest version of fastapi", registry: "pypi" })

// Capability triage — then follow up with get_context
search_packages({ query: "does zod@4 have IP address validation" })`,
            }}
          />
        </section>

        {/* Deployment — self-host */}
        <section id="self-host" className="scroll-mt-20 space-y-4">
          <div>
            <p className="page-eyebrow">Deployment</p>
            <h2 className="mt-2 text-lg font-semibold text-ink">Self-host with our help</h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-dim">
              Want indexing, retrieval, and the MCP endpoint inside your own network? Contact us and
              we&apos;ll help you plan and set up a deployment for your infrastructure, private
              repositories, storage, models, and access requirements.
            </p>
          </div>
          <div className="grid border border-rule bg-rule md:grid-cols-3 md:gap-px">
            <article className="bg-panel p-5">
              <h3 className="text-sm font-semibold text-ink">Your infrastructure</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Run on your VPC or on-prem. Data lives in your Postgres; payloads go to your
                filesystem or S3-compatible storage.
              </p>
            </article>
            <article className="border-t border-rule bg-panel p-5 md:border-t-0">
              <h3 className="text-sm font-semibold text-ink">Your private code</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Index private GitHub, GitLab (including self-hosted instances), and Azure DevOps
                repos alongside public registry packages.
              </p>
            </article>
            <article className="border-t border-rule bg-panel p-5 md:border-t-0">
              <h3 className="text-sm font-semibold text-ink">Your models and keys</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">
                Model selection is env-driven per role, and organizations with API keys control who
                can query each index.
              </p>
            </article>
          </div>
          <div className="flex flex-col gap-3 border border-accent/30 bg-accent-soft px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-ink">
                Self-hosting documentation is coming soon.
              </p>
              <p className="text-sm leading-relaxed text-dim">
                Until then, work directly with us to get your deployment running.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigateTo('/contact')}
              className="flex-shrink-0 bg-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              Contact us
            </button>
          </div>
        </section>

        {/* Need help */}
        <section className="border border-rule bg-panel px-5 py-4">
          <p className="text-sm font-semibold text-ink">Need help?</p>
          <p className="mt-1 text-sm leading-relaxed text-dim">
            Self-hosting documentation is coming soon.{' '}
            <a href="/contact" className="font-medium text-accent hover:underline">
              Contact us
            </a>{' '}
            and we&apos;ll help you plan and set up your deployment.
          </p>
        </section>
      </div>

      {/* ── Right "On this page" rail ── */}
      <nav aria-label="On this page" className="hidden xl:block">
        <div className="sticky top-20">
          <p className="mb-2 text-2xs font-semibold uppercase tracking-[0.12em] text-dim">
            On this page
          </p>
          <ul className="space-y-1.5 border-l border-rule pl-3">
            {ON_THIS_PAGE.map((item) => (
              <li key={item.target}>
                <button
                  type="button"
                  onClick={() => scrollToId(item.target)}
                  className="block w-full text-left text-xs leading-relaxed text-dim transition-colors hover:text-ink"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
